import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ImageOff, Pencil, Plus, Trash2 } from "lucide-react";
import swal from "sweetalert";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import { deletePromoPopup, getPromoPopups } from "../../../redux/promoPopups/action";
import HomePageTabs from "../Banners/HomePageTabs";
import { AUDIENCE_OPTIONS, SURFACE_FILTER_OPTIONS, audienceLabel } from "./constants";
import { findShowingNowId, rowState } from "./popupState";
import "../DynamicSections/HomePageCRM.css";
import "./PromoPopups.css";

const PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 350;

const formatDateRange = (startsAt, endsAt) => {
  if (!startsAt && !endsAt) return "Always";
  const fmt = (raw) =>
    raw
      ? new Date(raw).toLocaleDateString("en-GB", { month: "short", day: "numeric" })
      : "—";
  return `${fmt(startsAt)} – ${fmt(endsAt)}`;
};

const buildQueryParams = (search) => {
  const out = { page: Number(search.get("page")) || 1, per_page: PER_PAGE };
  const searchKey = search.get("search_key");
  if (searchKey) out.search_key = searchKey;
  const audience = search.get("audience");
  if (audience) out.audience = audience;
  const surface = search.get("surface");
  if (surface) out.surface = surface;
  const isActive = search.get("is_active");
  if (isActive) out.is_active = isActive;
  return out;
};

const PromoPopupsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [search, setSearch] = useSearchParams();
  const { hasAction } = usePermissions();

  const params = useMemo(() => buildQueryParams(search), [search]);
  const editable = hasAction(MENU.HOME, PERMISSION.EDIT);

  const popups = useSelector(
    (state) => state.reusableSlice?.promoPopups?.data || [],
  );
  const pagination = useSelector(
    (state) => state.reusableSlice?.promoPopups?.pagination || null,
  );

  const [searchDraft, setSearchDraft] = useState(params.search_key || "");
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPopups = useCallback(() => {
    dispatch(getPromoPopups(params, setIsLoading));
  }, [dispatch, params]);

  useEffect(() => {
    fetchPopups();
  }, [fetchPopups]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = searchDraft.trim();
      const current = search.get("search_key") || "";
      if (trimmed === current) return;
      const next = new URLSearchParams(search);
      if (trimmed) next.set("search_key", trimmed);
      else next.delete("search_key");
      next.set("page", "1");
      setSearch(next, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setSearch(next);
  };

  const changePage = (page) => {
    const next = new URLSearchParams(search);
    next.set("page", String(page));
    setSearch(next);
  };

  const handleDelete = (popup) => {
    swal({
      title: "Delete popup",
      text: `Remove "${popup.name}" permanently? To take it off the site without deleting it, switch it off instead.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((confirm) => {
      if (!confirm) return;
      dispatch(
        deletePromoPopup(
          popup.id,
          (val) => setDeletingId(val ? popup.id : null),
          fetchPopups,
        ),
      );
    });
  };

  const totalPages = pagination?.pages || 1;
  const startIndex = (params.page - 1) * PER_PAGE;
  const columnCount = editable ? 10 : 9;

  const showingNowId = useMemo(
    () => findShowingNowId(popups, params),
    [popups, params],
  );

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div>
          <h4>Home Page</h4>
          <p>
            Popups that appear a few seconds after someone lands. Only one shows
            at a time — the live campaign nearest the top of this list.
          </p>
        </div>
        {editable && (
          <button className="ds-btn-primary" onClick={() => navigate("/promo-popups/create")}>
            <Plus size={16} /> New Popup
          </button>
        )}
      </div>

      <HomePageTabs />

      <div className="ds-filters">
        <input
          type="text"
          placeholder="Search by campaign name or headline..."
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          style={{ width: 280 }}
        />
        <select
          value={params.audience || ""}
          onChange={(e) => updateFilter("audience", e.target.value)}
        >
          <option value="">All Audiences</option>
          {AUDIENCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={params.surface || ""}
          onChange={(e) => updateFilter("surface", e.target.value)}
        >
          <option value="">All Surfaces</option>
          {SURFACE_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={params.is_active || ""}
          onChange={(e) => updateFilter("is_active", e.target.value)}
        >
          <option value="">On and off</option>
          <option value="true">Switched on</option>
          <option value="false">Switched off</option>
        </select>
      </div>

      <div className="ds-table-wrapper">
        <table className="ds-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Preview</th>
              <th>Campaign</th>
              <th>Audience</th>
              <th>Surface</th>
              <th>Coupon</th>
              <th>Timing</th>
              <th>Status</th>
              <th>Schedule</th>
              {editable && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columnCount} style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                  Loading popups...
                </td>
              </tr>
            ) : popups.length === 0 ? (
              <tr>
                <td colSpan={columnCount} style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                  No popups yet. {editable ? "Create one to run your first campaign." : ""}
                </td>
              </tr>
            ) : (
              popups.map((popup, i) => {
                const state = rowState(popup, showingNowId);
                return (
                  <tr key={popup.id}>
                    <td>{startIndex + i + 1}</td>
                    <td>
                      {popup.image || popup.ar_image ? (
                        <img
                          src={popup.image || popup.ar_image}
                          alt=""
                          className="pp-thumb"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="pp-thumb--empty" title="Text-only popup">
                          <ImageOff size={14} />
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="pp-name">{popup.name}</div>
                      {popup.headline ? (
                        <div className="pp-name__sub">{popup.headline}</div>
                      ) : null}
                    </td>
                    <td>
                      <span className="ds-badge ds-badge--surface">
                        {audienceLabel(popup.audience)}
                      </span>
                    </td>
                    <td>
                      <span className="ds-badge ds-badge--surface">{popup.surface}</span>
                    </td>
                    <td>
                      {popup.coupon_code ? (
                        <code className="pp-code">{popup.coupon_code}</code>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      )}
                    </td>
                    <td className="pp-timing">
                      <span>{popup.delay_seconds}s delay</span>
                      <span>
                        {popup.frequency_days === 0
                          ? "returns next visit"
                          : `hidden ${popup.frequency_days}d after close`}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`ds-badge pp-state pp-state--${state.tone}`}
                        title={state.title || undefined}
                      >
                        {state.label}
                      </span>
                    </td>
                    <td>
                      <span className="ds-date-range">
                        {formatDateRange(popup.starts_at, popup.ends_at)}
                      </span>
                    </td>
                    {editable && (
                      <td>
                        <div className="ds-actions">
                          <button
                            className="ds-btn-edit"
                            onClick={() => navigate(`/promo-popups/edit/${popup.id}`)}
                            aria-label={`Edit popup ${popup.name}`}
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            className="ds-btn-delete"
                            disabled={deletingId === popup.id}
                            onClick={() => handleDelete(popup)}
                            aria-label={`Delete popup ${popup.name}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {pagination && totalPages > 1 && (
          <div className="ds-pagination">
            <span>
              Page {params.page} of {totalPages} ({pagination.total} popups)
            </span>
            <div className="ds-pagination-buttons">
              <button disabled={params.page <= 1} onClick={() => changePage(params.page - 1)}>
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                .filter((p) => Math.abs(p - params.page) <= 2 || p === 1 || p === totalPages)
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span style={{ padding: "0 4px" }}>...</span>
                    )}
                    <button
                      className={params.page === p ? "active" : ""}
                      onClick={() => changePage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
              <button
                disabled={params.page >= totalPages}
                onClick={() => changePage(params.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoPopupsList;
